/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Fri May 23 2025
 **/

import { useCallback, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import debounce from "lodash-es/debounce";

import { useChatMessageActions, useDMRoomState, useSpaceRoomState } from "@/frameworks/core/chat";
import { useRoomViewActions } from "@/frameworks/core/hooks";
import { Column } from "@/frameworks/designStuff/components";
import { Properties } from "@/frameworks/libraries/properties";
import ThreadMessageList from "@/modules/chat/components/Common/ThreadMessageList/ThreadMessageList";
import { useChatDataStore } from "@/modules/chat/store";

import { MessageInput } from "../../../../../Common";

export default function FullSizeThread({
	channelID,
	threadID,
	spaceID,
	type,
	activityMessageID
}: {
	channelID: string;
	threadID: string;
	spaceID?: string;
	type: "DM" | "Space";
	activityMessageID?: string;
}) {
	const { threadReaded } = useChatMessageActions();
	const { fullSizeChannelID, setFullSizeChannelID } = useChatDataStore(
		useShallow((state) => ({
			fullSizeChannelID: state.fullsize_chat_id,
			setFullSizeChannelID: state.set_fullsize_chat_id
		}))
	);
	const { updateRoomView } = useRoomViewActions();

	const spaceRoom = useSpaceRoomState({ spaceID: spaceID || "", roomID: channelID });
	const dmRoom = useDMRoomState(channelID);

	const channelData = spaceRoom ?? dmRoom;

	useEffect(() => {
		if (fullSizeChannelID && channelID !== fullSizeChannelID) {
			updateRoomView({
				roomId: fullSizeChannelID as string,
				deviceKey: Properties.browserTabID,
				view: false
			});
		}

		setFullSizeChannelID(channelID);
	}, [channelID]);

	const handleShowRecentThreadMessage = useCallback(
		debounce(() => {
			threadReaded({ roomId: channelID, threadMessageId: threadID });
		}, 1000),
		[dmRoom?.messageLastReadId]
	);

	if (!channelData) return;
	const channelUserList = channelData.users ?? [];
	const userListForMention = channelUserList.map((user) => ({
		name: user.name,
		src: user.profileImagePath,
		userID: user.id
	}));
	return (
		<Column css={{ width: "100%", height: "100%" }}>
			<ThreadMessageList
				variant="fullSize"
				roomID={channelID}
				isMyRoom={channelData?.myRoom}
				spaceID={spaceID}
				threadID={threadID}
				notice={channelData.notice}
				voteNotice={channelData.voteNotice}
				myRoomID={channelData.myRoomId}
				onShowRecentMessage={handleShowRecentThreadMessage}
				activityMessageID={activityMessageID}
			/>
			<MessageInput
				id={channelID}
				type={type}
				userList={userListForMention}
				variant="fullSize"
				myRoomID={channelData.myRoomId}
				isMyRoom={channelData.myRoom}
				threadID={threadID}
			/>
		</Column>
	);
}
